import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001') + '/api';

function App() {
  const [stations, setStations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentView, setCurrentView] = useState("browse");
  const [apiStatus, setApiStatus] = useState(null);
  const [stationStatus, setStationStatus] = useState({});
  const [validatingStations, setValidatingStations] = useState(new Set());
  const [deviceId, setDeviceId] = useState("");
  const audioRef = useRef(null);

  // Generate or retrieve unique device ID
  const getDeviceId = () => {
    let storedDeviceId = localStorage.getItem('radio_device_id');
    
    if (!storedDeviceId) {
      // Generate unique device ID based on multiple factors
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Device fingerprint', 2, 2);
      
      const canvasFingerprint = canvas.toDataURL();
      const screenFingerprint = `${screen.width}x${screen.height}x${screen.colorDepth}`;
      const timezoneFingerprint = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const languageFingerprint = navigator.language;
      const platformFingerprint = navigator.platform;
      const userAgentFingerprint = navigator.userAgent.slice(0, 50); // First 50 chars
      
      const combinedFingerprint = `${canvasFingerprint}-${screenFingerprint}-${timezoneFingerprint}-${languageFingerprint}-${platformFingerprint}-${userAgentFingerprint}`;
      
      // Create hash of the fingerprint
      let hash = 0;
      for (let i = 0; i < combinedFingerprint.length; i++) {
        const char = combinedFingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      
      // Add timestamp for additional uniqueness
      const timestamp = Date.now();
      const randomNum = Math.floor(Math.random() * 10000);
      
      storedDeviceId = `device_${Math.abs(hash)}_${timestamp}_${randomNum}`;
      localStorage.setItem('radio_device_id', storedDeviceId);
      
      console.log(`🔐 Generated new device ID: ${storedDeviceId}`);
    } else {
      console.log(`🔐 Using existing device ID: ${storedDeviceId}`);
    }
    
    return storedDeviceId;
  };

  useEffect(() => {
    // Initialize device ID first
    const deviceIdGenerated = getDeviceId();
    setDeviceId(deviceIdGenerated);
    
    // Then fetch data
    fetchCountries();
    fetchStations();
    fetchFavorites(deviceIdGenerated);
    fetchApiStatus();
  }, []);

  const fetchApiStatus = async () => {
    try {
      const response = await axios.get(`${API}/radio/api-status`);
      setApiStatus(response.data);
    } catch (error) {
      console.error("Error fetching API status:", error);
    }
  };

  const validateStationStatus = async (stationUuid) => {
    if (stationStatus[stationUuid] || validatingStations.has(stationUuid)) {
      return;
    }

    setValidatingStations(prev => new Set(prev).add(stationUuid));
    
    try {
      const response = await axios.get(`${API}/radio/validate/${stationUuid}`);
      setStationStatus(prev => ({
        ...prev,
        [stationUuid]: {
          valid: response.data.valid,
          error: response.data.error,
          checkedAt: Date.now()
        }
      }));
    } catch (error) {
      console.error(`Error validating station ${stationUuid}:`, error);
      setStationStatus(prev => ({
        ...prev,
        [stationUuid]: {
          valid: false,
          error: "Validation failed",
          checkedAt: Date.now()
        }
      }));
    } finally {
      setValidatingStations(prev => {
        const newSet = new Set(prev);
        newSet.delete(stationUuid);
        return newSet;
      });
    }
  };

  const getStationStatus = (stationUuid) => {
    const status = stationStatus[stationUuid];
    if (!status) return { valid: null, checking: validatingStations.has(stationUuid) };
    
    const isExpired = Date.now() - status.checkedAt > 10 * 60 * 1000;
    if (isExpired) {
      return { valid: null, checking: false };
    }
    
    return { valid: status.valid, error: status.error, checking: false };
  };

  const searchStations = async (country = "", name = "", language = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country) params.append("country", country);
      if (name) params.append("name", name);
      if (language) params.append("language", language);
      
      const baseLimit = 100;
      params.append("limit", baseLimit.toString());
      
      const response = await axios.get(`${API}/radio/stations/search?${params}`);
      setStations(response.data.stations);
      
      console.log(`🎵 Loaded ${response.data.stations.length} stations`);
      
      if (response.data.stations.length === 0 && !country && !name && !language) {
        console.log("No stations found, loading default popular stations...");
        const defaultResponse = await axios.get(`${API}/radio/stations/search?limit=${baseLimit}`);
        setStations(defaultResponse.data.stations);
      }
      
    } catch (error) {
      console.error("Error searching stations:", error);
      setStations([]);
    }
    setLoading(false);
  };

  const fetchStations = () => searchStations();
  
  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/radio/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchFavorites = async (deviceIdToUse = deviceId) => {
    if (!deviceIdToUse) return;
    
    try {
      const response = await axios.get(`${API}/radio/favorites?device_id=${deviceIdToUse}`);
      setFavorites(response.data);
      console.log(`📱 Loaded ${response.data.length} favorites for device: ${deviceIdToUse}`);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      setFavorites([]); // Set empty array on error
    }
  };

  const addToFavorites = async (station) => {
    if (!deviceId) {
      console.error("Device ID not available");
      return;
    }
    
    try {
      await axios.post(`${API}/radio/favorites`, {
        device_id: deviceId,
        station_uuid: station.stationuuid,
        station_name: station.name,
        station_url: station.url_resolved || station.url,
        station_country: station.country
      });
      fetchFavorites();
      console.log(`❤️ Added "${station.name}" to favorites for device: ${deviceId}`);
    } catch (error) {
      console.error("Error adding to favorites:", error);
    }
  };

  const removeFromFavorites = async (stationUuid) => {
    if (!deviceId) {
      console.error("Device ID not available");
      return;
    }
    
    try {
      await axios.delete(`${API}/radio/favorites/${stationUuid}?device_id=${deviceId}`);
      fetchFavorites();
      console.log(`💔 Removed station from favorites for device: ${deviceId}`);
    } catch (error) {
      console.error("Error removing from favorites:", error);
    }
  };

  const isFavorite = (stationUuid) => {
    return favorites.some(fav => fav.station_uuid === stationUuid);
  };

  const playStation = async (station) => {
    try {
      setLoading(true);
      
      const status = getStationStatus(station.stationuuid);
      
      if (status.valid === false) {
        const userConfirm = window.confirm(
          `⚠️ Station May Be Offline\n\n"${station.name}" previously failed to connect.\n\nError: ${status.error}\n\nWould you like to try anyway?`
        );
        if (!userConfirm) {
          setLoading(false);
          return;
        }
      }
      
      const clickResponse = await axios.post(`${API}/radio/click/${station.stationuuid}`);
      
      if (audioRef.current) {
        if (audioRef.current.setManualStop) {
          audioRef.current.setManualStop();
        }
        audioRef.current.pause();
        audioRef.current.src = "";
      }

      const streamUrl = clickResponse.data.url || station.url_resolved || station.url;
      
      if (!streamUrl) {
        alert(`🚫 No Stream URL\n\nNo valid streaming URL found for "${station.name}".`);
        setLoading(false);
        return;
      }
      
      const audio = new Audio(streamUrl);
      audioRef.current = audio;
      
      audio.crossOrigin = "anonymous";
      
      let isManualStop = false;
      const audioInstance = audio;
      
      const loadingTimeout = setTimeout(() => {
        if (audioRef.current === audioInstance) {
          audio.pause();
          audio.src = "";
          
          setStationStatus(prev => ({
            ...prev,
            [station.stationuuid]: {
              valid: false,
              error: "Stream loading timeout",
              checkedAt: Date.now()
            }
          }));
          
          alert(`⏱️ Stream Timeout\n\n"${station.name}" took too long to load.`);
          setLoading(false);
          setIsPlaying(false);
        }
      }, 15000);
      
      const handleLoadStart = () => {
        console.log("Loading stream...");
      };
      
      const handleCanPlay = () => {
        if (audioRef.current === audioInstance) {
          clearTimeout(loadingTimeout);
          console.log("Stream ready to play");
          
          setStationStatus(prev => ({
            ...prev,
            [station.stationuuid]: {
              valid: true,
              error: null,
              checkedAt: Date.now()
            }
          }));
          
          setCurrentStation({
            ...station,
            ...clickResponse.data.station
          });
          setIsPlaying(true);
          setLoading(false);
        }
      };
      
      const handleError = (e) => {
        if (audioRef.current === audioInstance && !isManualStop) {
          clearTimeout(loadingTimeout);
          console.error("Audio error:", e);
          
          setStationStatus(prev => ({
            ...prev,
            [station.stationuuid]: {
              valid: false,
              error: "Stream playback error",
              checkedAt: Date.now()
            }
          }));
          
          alert(`🚫 Playback Error\n\n"${station.name}" failed to load.`);
          setIsPlaying(false);
          setLoading(false);
        }
      };
      
      const handleEnded = () => {
        if (audioRef.current === audioInstance) {
          setIsPlaying(false);
          setLoading(false);
        }
      };
      
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('error', handleError);
      audio.addEventListener('ended', handleEnded);
      
      audioInstance.setManualStop = () => {
        isManualStop = true;
      };

      audio.volume = 0.7;
      await audio.play();
      
    } catch (error) {
      console.error("Error playing station:", error);
      setLoading(false);
      
      setStationStatus(prev => ({
        ...prev,
        [station.stationuuid]: {
          valid: false,
          error: "Connection error",
          checkedAt: Date.now()
        }
      }));
      
      alert(`❌ Connection Error\n\nFailed to connect to "${station.name}".`);
    }
  };

  const stopStation = () => {
    if (audioRef.current) {
      if (audioRef.current.setManualStop) {
        audioRef.current.setManualStop();
      }
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentStation(null);
  };

  const handleSearch = () => {
    searchStations(selectedCountry, searchTerm, selectedLanguage);
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    searchStations(country, searchTerm, selectedLanguage);
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    searchStations(selectedCountry, searchTerm, language);
  };

  const renderStationStatus = (status, station) => {
    if (status.checking) {
      return (
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-yellow-400">Checking...</span>
        </div>
      );
    } else if (status.valid === true) {
      return (
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-xs text-green-400">Verified</span>
        </div>
      );
    } else if (status.valid === false) {
      return (
        <div className="flex items-center gap-1" title={`Previous issue: ${status.error}`}>
          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
          <span className="text-xs text-orange-400">May be offline</span>
        </div>
      );
    } else {
      return (
        <div 
          className="flex items-center gap-1 cursor-pointer hover:opacity-80" 
          onClick={() => validateStationStatus(station.stationuuid)}
          title="Click to check status"
        >
          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
          <span className="text-xs text-gray-400">Check Status</span>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-8">
          <div className="flex items-center space-x-2 mb-4 sm:mb-0">
            <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">🎵</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">shibamemegen radio service</h1>
            
            {validatingStations.size > 0 && (
              <div className="flex items-center space-x-2 text-sm">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-yellow-400">
                  {validatingStations.size > 15 ? 'Verifying stations...' : `Checking ${validatingStations.size} stations...`}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-4">
            <button
              onClick={() => setCurrentView("browse")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentView === "browse"
                  ? "bg-blue-600 text-white"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              Browse ({stations.length})
            </button>
            <button
              onClick={() => setCurrentView("favorites")}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                currentView === "favorites"
                  ? "bg-blue-600 text-white"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              Favorites ({favorites.length})
            </button>
          </div>
        </div>

        {/* Current Player */}
        {currentStation && (
          <div className="bg-black/30 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-2xl">📻</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{currentStation.name}</h3>
                  <p className="text-white/70">
                    {currentStation.country} • {currentStation.language} • {currentStation.codec}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {isPlaying && (
                  <div className="flex space-x-1">
                    <div className="w-2 h-6 bg-green-500 rounded animate-pulse"></div>
                    <div className="w-2 h-4 bg-green-500 rounded animate-pulse delay-75"></div>
                    <div className="w-2 h-8 bg-green-500 rounded animate-pulse delay-150"></div>
                  </div>
                )}
                <span className="text-white font-medium">
                  {isPlaying ? "Live" : "Stopped"}
                </span>
              </div>
            </div>
            
            <div className="flex justify-center">
              <button
                onClick={stopStation}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <span>⏹️</span>
                <span>Stop Playing</span>
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-black/20 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">
                Search by name
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="e.g., NPR, BBC, KEXP"
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">
                Filter by country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Countries</option>
                {countries.slice(0, 20).map((country) => (
                  <option key={country.name} value={country.name} className="bg-gray-800">
                    {country.name} ({country.stationcount})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">
                Filter by language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Languages</option>
                <option value="english" className="bg-gray-800">English</option>
                <option value="spanish" className="bg-gray-800">Spanish</option>
                <option value="french" className="bg-gray-800">French</option>
                <option value="german" className="bg-gray-800">German</option>
                <option value="italian" className="bg-gray-800">Italian</option>
                <option value="portuguese" className="bg-gray-800">Portuguese</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleSearch}
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white py-2 px-4 rounded-lg font-medium transition-all"
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>

        {/* Station Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentView === "browse" ? (
            stations.map((station) => {
              const status = getStationStatus(station.stationuuid);
              
              return (
                <div
                  key={station.stationuuid}
                  className={`bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all ${
                    status.valid === true ? 'station-card-online' :
                    status.valid === false ? 'station-card-warning' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold text-white line-clamp-2">
                          {station.name}
                        </h3>
                        {renderStationStatus(status, station)}
                      </div>
                      <div className="space-y-1">
                        <p className="text-white/70 text-sm">
                          📍 {station.country}
                        </p>
                        {station.language && (
                          <p className="text-white/70 text-sm">
                            🗣️ {station.language}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-xs">
                          <span className="text-white/60">
                            👥 {station.clickcount} listeners
                          </span>
                          {station.bitrate > 0 && (
                            <span className={`px-2 py-1 rounded ${
                              station.bitrate >= 128 ? 'bg-green-600/20 text-green-400' :
                              station.bitrate >= 64 ? 'bg-yellow-600/20 text-yellow-400' :
                              'bg-red-600/20 text-red-400'
                            }`}>
                              {station.bitrate}kbps
                            </span>
                          )}
                          {station.codec && (
                            <span className="text-white/50">
                              {station.codec.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {station.verification_status === 'verified' && (
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-green-400 text-xs">✅ Verified Working</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => isFavorite(station.stationuuid) 
                        ? removeFromFavorites(station.stationuuid)
                        : addToFavorites(station)
                      }
                      className={`p-2 rounded-lg transition-colors ${
                        isFavorite(station.stationuuid)
                          ? "text-red-400 hover:text-red-300"
                          : "text-white/50 hover:text-white"
                      }`}
                    >
                      {isFavorite(station.stationuuid) ? "❤️" : "🤍"}
                    </button>
                  </div>
                  
                  <button
                    onClick={() => playStation(station)}
                    disabled={currentStation?.stationuuid === station.stationuuid && isPlaying}
                    className={`w-full py-3 rounded-lg font-medium transition-all ${
                      currentStation?.stationuuid === station.stationuuid && isPlaying
                        ? "bg-gray-600 text-gray-300 cursor-not-allowed"
                        : status.valid === true
                        ? "bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                        : status.valid === false
                        ? "bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white"
                        : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                    }`}
                  >
                    {currentStation?.stationuuid === station.stationuuid && isPlaying 
                      ? "🎵 Playing..." 
                      : status.valid === true
                      ? "▶️ Play (Verified)"
                      : status.valid === false
                      ? "⚠️ Try Anyway"
                      : "▶️ Play"
                    }
                  </button>
                </div>
              );
            })
          ) : (
            favorites.map((favorite) => (
              <div
                key={favorite.id}
                className="bg-black/20 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">
                      {favorite.station_name}
                    </h3>
                    <p className="text-white/70 text-sm">
                      Added {new Date(favorite.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => removeFromFavorites(favorite.station_uuid)}
                    className="text-red-400 hover:text-red-300 p-2 rounded-lg transition-colors"
                  >
                    ❤️
                  </button>
                </div>
                
                <button
                  onClick={() => playStation({
                    stationuuid: favorite.station_uuid,
                    name: favorite.station_name,
                    url: favorite.station_url,
                    country: favorite.station_country
                  })}
                  className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white py-3 rounded-lg font-medium transition-all"
                >
                  ▶️ Play Favorite
                </button>
              </div>
            ))
          )}
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p className="text-white mt-2">Loading stations...</p>
          </div>
        )}

        {!loading && stations.length === 0 && currentView === "browse" && (
          <div className="text-center py-8">
            <p className="text-white/70 text-lg">No stations found. Try a different search or filter.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;